import React, { useCallback, useEffect, useState } from 'react';
import {
  Gift, Copy, Check, Users, ShoppingBag, Wallet, Clock, ArrowUpRight, Loader2, Send, Store, Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '@/lib/authFetch';
import { REFERRAL_DISCOUNT, commissionRange, tierRanges, percent, usd, PAYOUT_METHODS } from '@/config/earnings';

// ─── Earn ─────────────────────────────────────────────────────────────────────
// Everything an account earns, in one place: the referral code (friends get a
// discount, you get a commission), marketplace sales, the balance they add up
// to, and payouts. One balance, one payout, whichever way it was earned.

const SOURCE_LABEL = { referral: 'Referral', marketplace: 'Marketplace' };
const STATUS_TONE = {
  pending: 'text-zinc-400', requested: 'text-amber-300', paid: 'text-emerald-400', void: 'text-zinc-600',
  rejected: 'text-red-400',
};

function shortDate(iso) {
  return iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
}

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-4">
      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
        <Icon className="w-3.5 h-3.5" /> {label}
      </p>
      <p className="mt-1.5 text-2xl font-bold text-white tabular-nums">{value}</p>
      {hint && <p className="text-[11px] text-zinc-600 mt-0.5 leading-snug">{hint}</p>}
    </div>
  );
}

// Payouts are by bank transfer only. PayPal and USDT were offered once, and a
// payout made that way still names them in the history.
const OLD_METHOD_LABELS = { paypal: 'PayPal', crypto: 'USDT' };
const methodName = (id) => PAYOUT_METHODS.find(m => m.id === id)?.label || OLD_METHOD_LABELS[id] || id;

const FIELD_LABELS = {
  bankName: 'Bank name', accountNumber: 'Account number', accountName: 'Account name', country: 'Country',
};
const FIELD_HINTS = {
  accountName: 'As it appears on the account',
};

function PayoutDetailsForm({ methods, current, onSaved }) {
  const [method, setMethod] = useState(methods.some(m => m.id === current?.method) ? current.method : methods[0]?.id || 'bank');
  const [values, setValues] = useState(current || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const spec = methods.find(m => m.id === method) || methods[0];

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const details = Object.fromEntries(spec.fields.map(f => [f, values[f] || '']));
      const { payoutDetails } = await authFetch('/api/earnings/payout-details', {
        method: 'PUT', body: JSON.stringify({ method, details }),
      });
      toast.success('Payout details saved.');
      onSaved(payoutDetails);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-3">
      {/* A choice only when there is one to make. */}
      {methods.length > 1 && (
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Payout method">
          {methods.map(m => (
            <button type="button" key={m.id} role="radio" aria-checked={method === m.id} onClick={() => setMethod(m.id)}
              className={`h-9 px-3.5 rounded-full text-xs font-semibold border transition-colors ${
                method === m.id ? 'bg-white text-zinc-950 border-white' : 'border-zinc-700 text-zinc-400 hover:text-white'
              }`}>
              {m.label}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {spec.fields.map(f => (
          <div key={f}>
            <label className="block">
              <span className="block text-xs font-medium text-zinc-400 mb-1">{FIELD_LABELS[f] || f}</span>
              <input value={values[f] || ''} onChange={(e) => setValues(v => ({ ...v, [f]: e.target.value }))}
                inputMode={f === 'accountNumber' ? 'numeric' : undefined}
                autoComplete="off"
                aria-describedby={FIELD_HINTS[f] ? `payout-${f}-hint` : undefined}
                className="w-full h-10 px-3 rounded-xl border border-zinc-700 bg-zinc-950 text-sm text-white outline-none focus:border-orange-500" />
            </label>
            {FIELD_HINTS[f] && <span id={`payout-${f}-hint`} className="block text-[11px] text-zinc-600 mt-1">{FIELD_HINTS[f]}</span>}
          </div>
        ))}
      </div>
      {error && <p role="alert" className="text-xs text-red-400">{error}</p>}
      <button type="submit" disabled={saving}
        className="h-9 px-4 rounded-full bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-xs font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-60">
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save bank details
      </button>
    </form>
  );
}

export default function EarnSection({ onOpenListings }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await authFetch('/api/earnings'));
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load your earnings.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = (value, key) => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(key);
      toast.success(key === 'code' ? 'Code copied.' : 'Link copied.');
      setTimeout(() => setCopied(''), 2000);
    }).catch(() => toast.error('Could not copy.'));
  };

  const requestPayout = async () => {
    setRequesting(true);
    try {
      const { payout } = await authFetch('/api/earnings/payouts', { method: 'POST' });
      toast.success(`Payout of ${usd(payout.amount_usd)} requested. We will let you know when it is sent.`);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRequesting(false);
    }
  };

  if (error && !data) {
    return (
      <div className="p-6">
        <p role="alert" className="text-sm text-red-400">{error}</p>
        <button onClick={load} className="mt-3 text-sm text-orange-400 hover:text-orange-300">Try again</button>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading your earnings…</div>;
  }

  const { referral, balance, marketplace } = data;
  const tier = referral.tier || {};
  const next = tier.next;
  const toNext = next ? Math.max(next.minCustomers - referral.customers, 0) : 0;
  const progress = next ? Math.min(100, Math.round((referral.customers / next.minCustomers) * 100)) : 100;
  const canRequest = balance.available >= data.payoutMinimumUsd && !!data.payoutDetails;
  const shareText = `Build your website with AI on BuildersLab — use my code ${referral.code} for ${REFERRAL_DISCOUNT} off any plan.`;
  const share = encodeURIComponent(`${shareText} ${referral.shareUrl || ''}`.trim());

  return (
    <div className="p-4 sm:p-6 max-w-5xl space-y-5">
      <div>
        <h2 className="text-2xl font-extrabold tracking-tight text-white">Earn</h2>
        <p className="text-zinc-500 text-sm mt-1">Share your code, sell your sites, get paid.</p>
      </div>

      {/* ── The referral code ── */}
      <div className="rounded-3xl border border-orange-500/25 bg-gradient-to-br from-orange-500/[0.09] via-zinc-900 to-zinc-900 p-5 sm:p-7">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-1 min-w-0">
            <p className="flex items-center gap-2 text-sm font-semibold text-orange-300"><Gift className="w-4 h-4" /> Your referral code</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="font-mono text-3xl sm:text-4xl font-extrabold tracking-[0.15em] text-white">{referral.code || '—'}</span>
              <button onClick={() => copy(referral.code, 'code')} disabled={!referral.code}
                className="h-9 px-3 rounded-full bg-white text-zinc-950 text-xs font-bold inline-flex items-center gap-1.5">
                {copied === 'code' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copy code
              </button>
            </div>
            {referral.shareUrl && (
              <div className="mt-3 flex items-center gap-2 max-w-lg">
                <span className="flex-1 min-w-0 truncate rounded-xl border border-zinc-800 bg-zinc-950/70 px-3 py-2 text-xs font-mono text-zinc-400">{referral.shareUrl}</span>
                <button onClick={() => copy(referral.shareUrl, 'link')} aria-label="Copy link"
                  className="h-9 w-9 shrink-0 rounded-xl border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 grid place-items-center">
                  {copied === 'link' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-zinc-300" />}
                </button>
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <a href={`https://wa.me/?text=${share}`} target="_blank" rel="noopener noreferrer"
                className="h-8 px-3 rounded-full border border-zinc-700 text-xs font-semibold text-zinc-300 hover:text-white inline-flex items-center gap-1.5">
                WhatsApp <ArrowUpRight className="w-3 h-3" />
              </a>
              <a href={`https://x.com/intent/post?text=${share}`} target="_blank" rel="noopener noreferrer"
                className="h-8 px-3 rounded-full border border-zinc-700 text-xs font-semibold text-zinc-300 hover:text-white inline-flex items-center gap-1.5">
                X <ArrowUpRight className="w-3 h-3" />
              </a>
              <a href={`mailto:?subject=${encodeURIComponent(`${REFERRAL_DISCOUNT} off BuildersLab`)}&body=${share}`}
                className="h-8 px-3 rounded-full border border-zinc-700 text-xs font-semibold text-zinc-300 hover:text-white inline-flex items-center gap-1.5">
                <Mail className="w-3 h-3" /> Email
              </a>
            </div>
          </div>

          <ol className="lg:w-[340px] shrink-0 space-y-3">
            {[
              ['Share your code or link', 'With friends, clients, your audience.'],
              [`They get ${REFERRAL_DISCOUNT} off`, 'Any plan, monthly or yearly, card or crypto.'],
              [`You earn ${commissionRange()}`, `Of what they pay, every time they pay with your code. Paid out after ${referral.holdDays} days.`],
            ].map(([title, body], i) => (
              <li key={title} className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-orange-500 text-ink text-xs font-bold grid place-items-center shrink-0">{i + 1}</span>
                <span>
                  <span className="block text-sm font-semibold text-white">{title}</span>
                  <span className="block text-xs text-zinc-500 leading-relaxed">{body}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* ── Tier ── */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-white font-semibold">
            {tier.name} tier · <span className="text-orange-400">{percent(tier.rate)} commission</span>
          </p>
          <p className="text-xs text-zinc-500">
            {next ? `${toNext} more paying customer${toNext === 1 ? '' : 's'} to ${next.name} (${percent(next.rate)})` : 'Top tier — the highest commission'}
          </p>
        </div>
        <div className="mt-3 h-2 rounded-full bg-zinc-800 overflow-hidden" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label="Progress to the next tier">
          <div className="h-full rounded-full bg-orange-500 transition-[width] duration-500" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {tierRanges().map(t => (
            <span key={t.name} className={`text-[11px] rounded-full border px-2.5 py-1 ${t.name === tier.name ? 'border-orange-500/50 text-orange-300 bg-orange-500/10' : 'border-zinc-800 text-zinc-500'}`}>
              {t.name}: {t.range} customers · {percent(t.rate)}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Users} label="Signed up" value={referral.signups} hint="with your code or link" />
        <Stat icon={Users} label="Paying customers" value={referral.customers} hint="bought a plan with your code" />
        <Stat icon={Gift} label="Referral earnings" value={usd(referral.commissionUsd)} hint={`${referral.purchases} purchase${referral.purchases === 1 ? '' : 's'}`} />
        <Stat icon={Store} label="Marketplace" value={usd(marketplace.earnedUsd)} hint={`${marketplace.sales} sale${marketplace.sales === 1 ? '' : 's'}`} />
      </div>

      {/* ── Balance & payouts ── */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-5">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold"><Wallet className="w-3.5 h-3.5" /> Available to pay out</p>
            <p className="mt-1 text-4xl font-extrabold text-white tabular-nums">{usd(balance.available)}</p>
            <p className="mt-1 text-xs text-zinc-500">
              {usd(balance.held)} on hold · {usd(balance.requested)} requested · {usd(balance.paid)} paid out
            </p>
          </div>
          <div className="sm:text-right">
            <button onClick={requestPayout} disabled={!canRequest || requesting}
              className="h-11 px-5 rounded-full bg-orange-500 hover:bg-orange-400 disabled:opacity-40 disabled:cursor-not-allowed text-ink text-sm font-bold inline-flex items-center gap-2">
              {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Request payout
            </button>
            <p className="mt-1.5 text-[11px] text-zinc-600 max-w-[260px] sm:ml-auto">
              {!data.payoutDetails
                ? 'Add your bank details below first.'
                : balance.available < data.payoutMinimumUsd
                  ? `Payouts start from ${usd(data.payoutMinimumUsd)}.`
                  : 'Paid by bank transfer, usually within a few working days.'}
            </p>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-zinc-800">
          {data.payoutDetails && !editingDetails ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-zinc-300">
                Paid by <span className="text-white font-medium">bank transfer</span> to {data.payoutDetails.bankName} ····{String(data.payoutDetails.accountNumber || '').slice(-4)}
                {data.payoutDetails.accountName ? <span className="text-zinc-500"> ({data.payoutDetails.accountName})</span> : null}
              </p>
              <button onClick={() => setEditingDetails(true)} className="text-xs font-medium text-orange-400 hover:text-orange-300">Change</button>
            </div>
          ) : (
            <>
              <p className="text-sm text-white font-semibold mb-1">Where should we pay you?</p>
              <p className="text-xs text-zinc-500 mb-3">Payouts are sent by bank transfer.</p>
              <PayoutDetailsForm
                methods={data.payoutMethods || PAYOUT_METHODS}
                current={data.payoutDetails}
                onSaved={(details) => { setData(d => ({ ...d, payoutDetails: details })); setEditingDetails(false); }}
              />
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-5">
          <p className="text-white font-semibold mb-3">Recent earnings</p>
          {data.recent.length === 0 ? (
            <p className="text-sm text-zinc-500 leading-relaxed">Nothing yet. Commissions and sales show up here the moment they are paid.</p>
          ) : (
            <ul className="divide-y divide-zinc-800">
              {data.recent.map(e => (
                <li key={e.id} className="py-2.5 flex items-start justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block text-sm text-zinc-200 truncate">{e.description}</span>
                    <span className="block text-[11px] text-zinc-600">
                      {SOURCE_LABEL[e.source] || e.source} · {shortDate(e.created_at)}
                      {e.status === 'pending' && new Date(e.available_at) > new Date() && (
                        <> · <Clock className="inline w-3 h-3 -mt-px" /> available {shortDate(e.available_at)}</>
                      )}
                    </span>
                  </span>
                  <span className={`text-sm font-semibold tabular-nums shrink-0 ${STATUS_TONE[e.status] || 'text-white'}`}>{usd(e.amount_usd)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-white font-semibold">Payouts</p>
            {onOpenListings && (
              <button onClick={onOpenListings} className="text-xs font-medium text-orange-400 hover:text-orange-300 inline-flex items-center gap-1">
                <ShoppingBag className="w-3.5 h-3.5" /> My listings
              </button>
            )}
          </div>
          {data.payouts.length === 0 ? (
            <p className="text-sm text-zinc-500">No payouts yet.</p>
          ) : (
            <ul className="divide-y divide-zinc-800">
              {data.payouts.map(p => (
                <li key={p.id} className="py-2.5 flex items-center justify-between gap-3">
                  <span className="text-sm text-zinc-300">{shortDate(p.created_at)} · {methodName(p.method)}</span>
                  <span className="text-right">
                    <span className="block text-sm font-semibold text-white tabular-nums">{usd(p.amount_usd)}</span>
                    <span className={`block text-[11px] capitalize ${STATUS_TONE[p.status] || 'text-zinc-400'}`}>{p.status}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
